import { formatFactorOrDash, formatNumber } from '@/lib/format/number';
import { formatTextOrDash } from '@/lib/format/text';
import { formValue } from '@/lib/mappers/formValues';
import { CERTIFICATE_TYPE, certificateTypeLabel, materialTypeLabel } from '@/config/statuses';
import { isVolumeUnit, normalizeUnit } from '@/lib/utils/unitConversion';
import { MATERIAL_FIELDS, MATERIAL_TYPES } from '../constants/materials.constants';

const trimmed = (value) => (value ?? '').trim();

const unitSymbol = (unit) => (unit ? normalizeUnit(unit) : '');

const energyOf = (dto) => dto.totalEnergyPerKg ?? dto.totalEnergyPerLitre ?? null;

export const processMaterialRow = (dto) => ({
  id: dto.id,
  materialName: formatTextOrDash(dto.name),
  materialCode: formatTextOrDash(dto.material_id),
  materialType: formatTextOrDash(materialTypeLabel(dto.material_type)),
  certificateType: formatTextOrDash(certificateTypeLabel(dto.certificate_type)),
  declarationUnit: formatTextOrDash(unitSymbol(dto.unit)),
  workingUnit: formatTextOrDash(unitSymbol(dto.display_unit)),
  conversionFactor: formatFactorOrDash(dto.converted_weight_quantity),
  energyContent: energyOf(dto) != null ? formatNumber(energyOf(dto)) : '-',
  isEditable: dto.isEditable !== false,
});

export const processMaterials = (dto) => (dto?.output?.materials ?? []).map(processMaterialRow);

export const processMaterialForm = (dto) => {
  if (!dto) return null;

  const displayUnit = dto.display_units?.[0];
  const declarationUnit = dto.unit || displayUnit?.converted_unit_name || '';
  const workingUnit = displayUnit?.unit_name ?? '';

  return {
    id: dto.id,
    values: {
      [MATERIAL_FIELDS.materialName]: formValue(dto.name),
      [MATERIAL_FIELDS.materialId]: formValue(dto.material_id),
      [MATERIAL_FIELDS.referenceId]: formValue(dto.reference_id),
      [MATERIAL_FIELDS.materialType]: formValue(dto.material_type),
      [MATERIAL_FIELDS.certificateType]: formValue(dto.certificate_type),
      [MATERIAL_FIELDS.declarationUnit]: formValue(declarationUnit),
      [MATERIAL_FIELDS.workingUnit]: workingUnit === declarationUnit ? '' : formValue(workingUnit),
      [MATERIAL_FIELDS.conversionFactor]: formValue(displayUnit?.converted_weight_quantity),
      [MATERIAL_FIELDS.energyContent]: formValue(
        dto.total_energy_per_kg ?? dto.total_energy_per_litre,
      ),
    },
  };
};

const energyPayload = (declarationUnit, energyContent) => {
  const energy = Number(energyContent);
  if (!energyContent || Number.isNaN(energy)) return {};

  return isVolumeUnit(declarationUnit)
    ? { totalEnergyPerLitre: energy }
    : { totalEnergyPerKg: energy };
};

export const buildMaterialDto = (values) => {
  const referenceId = trimmed(values[MATERIAL_FIELDS.referenceId]);

  const material = {
    name: trimmed(values[MATERIAL_FIELDS.materialName]),
    materialId: trimmed(values[MATERIAL_FIELDS.materialId]),
    materialType: values[MATERIAL_FIELDS.materialType],
    ...(referenceId ? { referenceId } : {}),
  };

  if (values[MATERIAL_FIELDS.materialType] !== MATERIAL_TYPES.certified) return material;

  const declarationUnit = values[MATERIAL_FIELDS.declarationUnit];
  const workingUnit = values[MATERIAL_FIELDS.workingUnit];
  const factor = trimmed(values[MATERIAL_FIELDS.conversionFactor]);
  const certificateType = values[MATERIAL_FIELDS.certificateType];

  return {
    ...material,
    certificateType,
    unit: declarationUnit,
    displayUnits: [
      {
        unit_name: workingUnit || null,
        converted_unit_name: declarationUnit,
        converted_weight_quantity: factor ? Number(factor) : null,
      },
    ],
    ...(certificateType === CERTIFICATE_TYPE.isccEu
      ? energyPayload(declarationUnit, values[MATERIAL_FIELDS.energyContent])
      : {}),
  };
};
